/* eslint-disable react/jsx-no-bind */
import stylesUtils from 'shared-components/styles/util.module.css'
import { PageBodyContext } from 'shared-components/contexts/PageBodyContext'
import styles from './UnderNavBlock.module.css'
import ReadingProgressBar from 'shared-components/ui/ReadingProgressBar'
import DealBar from './DealBar'
import clsx from 'clsx'
import { DSLayoutProps } from 'shared-definitions/types'
import { ScrollUpContext } from 'shared-components/contexts/ScrollUpContext'
import BestBuyPanel from 'shared-components/layout/BestBuyPanel'
import { AffilateContext } from 'shared-components/contexts/AffilateContext'
import dynamic from 'next/dynamic'
import { useEffect, useRef, useState } from 'react'
import { DealBarContext } from 'shared-components/contexts/DealBarContext'
import { CommonDataContext } from 'shared-components/contexts/CommonDataContext'
import { observeScroll } from 'shared-code/observer'
import useDebouncedState from 'shared-components/hooks/use-debounced-state'
import { ThemeContext } from '../contexts/ThemeContext'
import { PostContext } from '../contexts/PostContext'
import { ResponsiveContext } from '../contexts/ResponsiveContext'
import { isCurrentlyVisible } from 'shared-code/responsive'
import { ConversationContext } from '../comments/conversation/ConversationContext'
import {
  RAIL_TOC_STACK_VAR,
  TOC_CLOSED_MAX_REM,
  railTocStack,
  tocClosedHeight,
  underNavTocShown,
} from './railStickyStack'

const TableOfContents = dynamic(() => import('shared-components/ui/TableOfContents'), {
  ssr: false,
})

interface UnderNavBlockProps {
  tocVisibility?: DSLayoutProps['tocVisibility']
  hideDealBar?: boolean
  dealBarVariant?: DSLayoutProps['dealBarVariant']
}

/*
- dealbar
- affilate bar
- mobile table of contents
*/
const UnderNavBlock: React.VFC<UnderNavBlockProps> = ({
  tocVisibility,
  hideDealBar,
  dealBarVariant,
}) => {
  const { ready, ...breakpoints } = ResponsiveContext.useContainer()
  const { showAdminPanel } = CommonDataContext.useContainer()
  const { scrolledUp } = ScrollUpContext.useContainer()
  const { showReadingProgress } = PageBodyContext.useContainer()
  const { sections } = PostContext.useContainer()
  const { bars, activeIndex, visible } = AffilateContext.useContainer()
  // Phones: while the conversation's top bar shows, it takes the deal bar's and the best-buy
  // panel's place (Guillaume, 2026-10-25). The panel is dropped, not hidden — a hidden panel
  // hides the TOC strip with it — so the TOC follows its own rules and the bar docks under it.
  const conversationBar =
    (ConversationContext.useOptionalContainer()?.barShown ?? false) && ready && !breakpoints.lg
  const bestBuyItem = conversationBar ? null : bars[activeIndex] ?? null
  const [focus, setFocus] = useState(false)
  const { dealbar, hiddenOnMobile: dealbarHiddenOnMobile } = DealBarContext.useContainer()
  const { theme } = ThemeContext.useContainer()

  const hasAffilate = bestBuyItem && visible
  const dealbarSticky = dealbar?.sticky === true && !conversationBar

  // tocVisible
  let tocVisible = ready
  if (ready && tocVisibility) {
    tocVisible = isCurrentlyVisible(breakpoints, tocVisibility)
  }

  // toc
  const [showToc, setShowToc] = useDebouncedState(false, 300)
  useEffect(() => {
    const header = document.querySelector('header') // hack
    if (!header) {
      // eslint-disable-next-line no-console
      console.warn('UnderNavBlock depends on header DOM element')
      return
    }

    const observerX2 = observeScroll(header, window.innerHeight, setShowToc)
    return () => {
      observerX2.disconnect()
    }
    // eslint rule disabled intentionally
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isHiddenTableOfContents =
    scrolledUp || (dealbarSticky && !dealbarHiddenOnMobile && !hasAffilate) || !showToc

  // The desktop rail stacks under this TOC (railStickyStack.ts): publish the line under its closed
  // box on <html> — it is viewport-fixed, outside the rail — 0 while it is absent or hidden.
  const innerRef = useRef<HTMLDivElement>(null)
  const tocClosedRef = useRef(0)
  const tocRendered = tocVisible && sections !== null && sections.items.length > 0
  const tocShown = underNavTocShown({
    rendered: tocRendered,
    hasPanel: bestBuyItem !== null,
    panelVisible: Boolean(hasAffilate),
    tocHidden: isHiddenTableOfContents,
  })
  // "Smallest closed height seen" belongs to one article's TOC at one width: forget it when the
  // sections change (SPA navigation to another article). Declared before the publish effect, so
  // it runs first on the same commit; the resize handler below forgets it too.
  useEffect(() => {
    tocClosedRef.current = 0
  }, [sections])
  useEffect(() => {
    const inner = innerRef.current
    const bar = inner?.parentElement
    if (!inner || !bar) {
      return undefined
    }
    const root = document.documentElement
    const publish = (): void => {
      const toc = inner.querySelector<HTMLElement>('[data-table-of-content-wrapper]')
      const capPx = TOC_CLOSED_MAX_REM * (parseFloat(getComputedStyle(root).fontSize) || 16)
      tocClosedRef.current = tocClosedHeight(toc?.offsetHeight ?? 0, tocClosedRef.current, capPx)
      const stack = railTocStack({
        shown: tocShown && toc !== null,
        // The bar's own `top` (progress bar / preview mode), not its rect: the rect is mid-slide
        // while the show transition runs.
        top: parseFloat(getComputedStyle(bar).top) || 0,
        closedHeight: tocClosedRef.current,
      })
      if (stack > 0) {
        root.style.setProperty(RAIL_TOC_STACK_VAR, `${stack}px`)
      } else {
        root.style.removeProperty(RAIL_TOC_STACK_VAR)
      }
    }
    publish()
    // The TOC chunk loads after mount (ssr: false) and opens/closes: re-measure on size changes.
    // The observer keeps the smallest height (an open TOC is taller than the closed line); a
    // window resize re-wraps the closed bar, so it measures afresh.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish)
    observer?.observe(inner)
    const onResize = (): void => {
      tocClosedRef.current = 0
      publish()
    }
    window.addEventListener('resize', onResize)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', onResize)
      root.style.removeProperty(RAIL_TOC_STACK_VAR)
    }
  }, [tocShown, showAdminPanel, showReadingProgress, sections])

  return (
    <>
      {showReadingProgress ? (
        <ReadingProgressBar
          className={clsx(styles.progress, {
            [styles.scrolledUp]: scrolledUp,
            [styles.previewMode]: showAdminPanel,
          })}
        />
      ) : null}
      {hideDealBar ? null : (
        <DealBar
          variant={dealBarVariant}
          className={clsx(styles.dealbar, {
            [styles.hasReadingProgress]: showReadingProgress,
            [styles.scrolledUp]: scrolledUp,
            [styles.hidden]: hasAffilate || conversationBar,
            [styles.previewMode]: showAdminPanel,
          })}
        />
      )}
      <div
        onBlur={() => setFocus(false)}
        onFocus={() => setFocus(true)}
        data-under-nav
        className={clsx(styles.underDealBar, {
          [styles.focused]: focus,
          [styles.previewMode]: showAdminPanel,
          [styles.hasReadingProgress]: showReadingProgress,
          [styles.hidden]: !hasAffilate && isHiddenTableOfContents,
        })}
      >
        <div ref={innerRef} className={clsx(stylesUtils.wrapper, styles.inner)}>
          {bestBuyItem ? (
            <BestBuyPanel
              product={bestBuyItem}
              className={clsx(styles.panel, { [styles.hidden]: !hasAffilate })}
            />
          ) : null}
          {tocVisible && sections && sections.items.length > 0 && (
            <TableOfContents
              sections={sections}
              variant="top"
              className={clsx(styles.table, stylesUtils.darkTheme, {
                [styles.tableDarkMode]: theme === 'dark',
                [styles.tableLightMode]: theme === 'light',
                [styles.hidden]: isHiddenTableOfContents,
              })}
            />
          )}
        </div>
      </div>
    </>
  )
}

export default UnderNavBlock
